import classNames from 'classnames';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HandoffForm } from '@/Domain/components/Action/HandoffAction/HandoffAction.sheet';
import { AutoSizeInput } from '@/Domain/components/Shared/AutosizeInput/AutosizeInput';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FormDescription, FormField, FormLabel } from '@/Domain/components/Shared/Form/Form';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';

type Props = {
  form: UseFormReturn<HandoffForm>;
};

const MAX_EMAILS = 100;
export const EmailNotificationField = ({ form }: Props) => {
  const { t } = useTranslation('handoff_action');

  const { fields, append, remove } = useFieldArray({
    name: 'settings.emails',
    control: form.control,
  });

  const handleAddField = () => append({ value: '' }, { focusIndex: fields.length });
  const handelRemoveField = (index: number) => remove(index);

  return (
    <div id="email_notification">
      <div className="flex items-center justify-between gap-4">
        <FormLabel>{t('form.email_notification_label')}</FormLabel>
        <FormField
          name="settings.send_email_notification"
          render={({ field }) => (
            <Switch
              id="settings.send_email_notification"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>
      <FormDescription>{t('form.email_notification_description')}</FormDescription>

      <div className="flex items-center flex-wrap gap-2 mt-2.5">
        {fields.map((arrayField, index) => (
          <FormField
            key={arrayField.id}
            name={`settings.emails.${index}.value`}
            render={({ field, fieldState }) => (
              <Badge
                onClose={() => handelRemoveField(index)}
                size="lg"
                variant="secondary"
                className={classNames('', {
                  '!border-red-500': fieldState.error,
                })}
              >
                <AutoSizeInput {...field} inputProps={{ autoComplete: 'off' }} />
              </Badge>
            )}
          />
        ))}

        <Tooltip open={fields.length >= MAX_EMAILS ? undefined : false}>
          <TooltipTrigger asChild>
            <div>
              <Button
                variant="link"
                onClick={handleAddField}
                className="!pl-0"
                disabled={fields.length >= MAX_EMAILS}
              >
                <AddIcon />
                {t('button.add_email')}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>{t('tooltip.max_emails_reached', { max: MAX_EMAILS })}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
